import axios from 'axios';
import { VouchersApiResponse, Voucher, VoucherStatistics, VoucherFormData, AdminApiResponse, VoucherStatus } from '../types';
import { API_CONFIG } from '../config/apiConfig';

const voucherApi = axios.create({
  baseURL: API_CONFIG.adminBaseURL,
  timeout: API_CONFIG.defaultTimeout,
});

// Request interceptor for adding auth headers
voucherApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminAccessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
voucherApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminAccessToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface VoucherQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  voucher_type?: string;
  sortBy?: 'created_at' | 'expires_at' | 'value';
  sortOrder?: 'asc' | 'desc';
}

export const voucherService = {
  // Get all vouchers with filtering and pagination
  async getVouchers(params: VoucherQueryParams = {}): Promise<VouchersApiResponse> {
    try {
      const response = await voucherApi.get('/vouchers', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      throw error;
    }
  },

  // Get voucher by ID
  async getVoucherById(id: string): Promise<AdminApiResponse<Voucher>> {
    try {
      const response = await voucherApi.get(`/vouchers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching voucher:', error);
      throw error;
    }
  },

  // Create new voucher
  async createVoucher(data: VoucherFormData): Promise<AdminApiResponse<Voucher>> {
    try {
      const response = await voucherApi.post('/vouchers', data);
      return response.data;
    } catch (error) {
      console.error('Error creating voucher:', error);
      throw error;
    }
  },

  // Update voucher
  async updateVoucher(id: string, data: Partial<VoucherFormData>): Promise<AdminApiResponse<Voucher>> {
    try {
      const response = await voucherApi.put(`/vouchers/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating voucher:', error);
      throw error;
    }
  },

  // Delete voucher
  async deleteVoucher(id: string): Promise<AdminApiResponse<null>> {
    try {
      const response = await voucherApi.delete(`/vouchers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting voucher:', error);
      throw error;
    }
  },

  // Update voucher status
  async updateVoucherStatus(id: string, status: VoucherStatus): Promise<AdminApiResponse<Voucher>> {
    try {
      const response = await voucherApi.patch(`/vouchers/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating voucher status:', error);
      throw error;
    }
  },

  // Bulk update vouchers
  async bulkUpdateVouchers(voucherIds: string[], updates: any): Promise<AdminApiResponse<any>> {
    try {
      const response = await voucherApi.patch('/vouchers/bulk', { voucherIds, updates });
      return response.data;
    } catch (error) {
      console.error('Error bulk updating vouchers:', error);
      throw error;
    }
  },

  // Get voucher statistics
  async getVoucherStatistics(): Promise<AdminApiResponse<VoucherStatistics>> {
    try {
      const response = await voucherApi.get('/vouchers/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching voucher statistics:', error);
      throw error;
    }
  },

  // Export vouchers
  async exportVouchers(format: 'csv' | 'json' | 'xlsx', filters?: any): Promise<AdminApiResponse<any>> {
    try {
      const response = await voucherApi.get('/vouchers/export', {
        params: { format, ...filters }
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting vouchers:', error);
      throw error;
    }
  }
};

export default voucherService;
